import React, { useState, useEffect, useRef } from 'react'; // Añadido useRef aquí
import PropTypes from 'prop-types';
import { useDrag } from 'react-dnd';
import { Icons } from '../Icons';

const Thumbnail = ({
  displaySetInstanceUID,
  className,
  imageSrc,
  imageAltText,
  description,
  seriesNumber,
  numInstances,
  loadingProgress,
  countIcon,
  messages,
  isActive,
  onClick,
  onDoubleClick,
  thumbnailType,
  modality,
  viewPreset = 'thumbnails',
  isHydratedForDerivedDisplaySet = false,
  isTracked = false,
  canReject = false,
  dragData = {},
  onReject = () => {},
  onClickUntrack = () => {},
  ThumbnailMenuItems = () => {},
}) => {
  const [collectedProps, drag, dragPreview] = useDrag({
    type: 'displayset',
    item: { ...dragData },
    canDrag: function (monitor) {
      return Object.keys(dragData).length !== 0;
    },
  });

  const [isMobile, setIsMobile] = useState(false);
  const [lastTap, setLastTap] = useState(0);
  const thumbnailRef = useRef(null);
  const imageContainerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const isMobileDevice = /(Mobi|Android|iPhone|iPad|iPod)/.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Efecto para forzar estilos
  useEffect(() => {
    if (thumbnailRef.current && imageContainerRef.current) {
      // Estilos del contenedor principal
      const thumb = thumbnailRef.current;
      thumb.style.setProperty('margin-bottom', '2rem', 'important');
      thumb.style.setProperty('display', 'flex', 'important');
      thumb.style.setProperty('flex', '1', 'important');
      thumb.style.setProperty('cursor', 'pointer', 'important');
      thumb.style.setProperty('user-select', 'none', 'important');
      thumb.style.setProperty('flex-direction', 'column', 'important');
      thumb.style.setProperty('padding-left', '0.25rem', 'important');
      thumb.style.setProperty('padding-right', '0.25rem', 'important');
      thumb.style.setProperty('outline', 'none', 'important');

      // Estilos del contenedor de imagen
      const imgContainer = imageContainerRef.current;
      imgContainer.style.setProperty('min-height', '2.5rem', 'important');
      imgContainer.style.setProperty('display', 'flex', 'important');
      imgContainer.style.setProperty('flex', '1', 'important');
      imgContainer.style.setProperty('align-items', 'center', 'important');
      imgContainer.style.setProperty('justify-content', 'center', 'important');
      imgContainer.style.setProperty('overflow', 'hidden', 'important');
      imgContainer.style.setProperty('border-radius', '0.375rem', 'important');
      imgContainer.style.setProperty('color', 'white', 'important');
      imgContainer.style.setProperty('font-size', '1rem', 'important');
      imgContainer.style.setProperty('position', 'relative', 'important');

      if (isActive) {
        imgContainer.style.setProperty('border', '2px solid #38bdf8', 'important');
      } else {
        imgContainer.style.setProperty('border', 'none', 'important');
      }

      if (isMobile) {
        imgContainer.style.setProperty('opacity', '0.9', 'important');
        imgContainer.style.setProperty('width', '70px', 'important');
        imgContainer.style.setProperty('height', '70px', 'important');
        imgContainer.style.setProperty('font-size', '9px', 'important');
      } else {
        imgContainer.style.setProperty('opacity', '1', 'important');
        imgContainer.style.setProperty('width', '70px', 'important');
        imgContainer.style.setProperty('height', '70px', 'important');
        imgContainer.style.setProperty('font-size', '10px', 'important');
      }
    }
  }, [isActive, isMobile]);

  const handleTouchEnd = e => {
    const currentTime = new Date().getTime();
    const tapLength = currentTime - lastTap;
    if (tapLength < 300 && tapLength > 0) {
      onDoubleClick(e);
    }
    setLastTap(currentTime);
  };

  return (
    <div
      ref={thumbnailRef}
      id={`thumbnail-${displaySetInstanceUID}`}
      data-cy={
        thumbnailType === 'thumbnailNoImage'
          ? 'study-browser-thumbnail-no-image'
          : 'study-browser-thumbnail'
      }
      data-series={seriesNumber}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onTouchEnd={handleTouchEnd}
      role="button"
      tabIndex="0"
    >
      <div ref={drag}>
        <div ref={imageContainerRef}>
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={imageAltText}
              style={{
                minHeight: '2.5rem',
                height: '100%',
                width: '100%',
                objectFit: 'cover',
              }}
              crossOrigin="anonymous"
            />
          ) : (
            <div>{imageAltText}</div>
          )}
          <div
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              top: 0,
              left: 0,
              height: '4rem',
              width: '100%',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.9), transparent)',
            }}
          ></div>
          <div
            style={{
              pointerEvents: 'none',
              position: 'absolute',
              bottom: 0,
              left: 0,
              height: '4rem',
              width: '100%',
              background: 'linear-gradient(to top, rgba(0,0,0,0.9), transparent)',
            }}
          ></div>
          <div
            style={{
              position: 'absolute',
              top: 0,
              right: '0.25rem',
              display: 'flex',
              alignItems: 'center',
            }}
          >
            {countIcon ? (
              React.createElement(Icons[countIcon] || Icons.MissingIcon, {
                style: { marginRight: '0.25rem', width: '0.75rem' },
              })
            ) : (
              <Icons.InfoSeries style={{ marginRight: '0.25rem', width: '0.75rem' }} />
            )}
            {numInstances}
          </div>
          <div
            style={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              textAlign: 'center',
              color: 'white',
            }}
          >
            {description}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            flex: '1',
            flexDirection: 'row',
            alignItems: 'center',
            color: '#7dd3fc',
            fontSize: '1rem',
          }}
        >
          <div
            style={{
              marginRight: '0.5rem',
              display: 'flex',
            }}
          >
            {loadingProgress && loadingProgress < 1 && <>{Math.round(loadingProgress * 100)}%</>}
            {loadingProgress && loadingProgress === 1 && (
              <Icons.Database style={{ width: '0.75rem' }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

Thumbnail.propTypes = {
  displaySetInstanceUID: PropTypes.string.isRequired,
  className: PropTypes.string,
  imageSrc: PropTypes.string,
  dragData: PropTypes.shape({
    type: PropTypes.string.isRequired,
  }),
  imageAltText: PropTypes.string,
  description: PropTypes.string.isRequired,
  seriesNumber: PropTypes.any,
  numInstances: PropTypes.number.isRequired,
  loadingProgress: PropTypes.number,
  messages: PropTypes.object,
  isActive: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  onDoubleClick: PropTypes.func.isRequired,
  viewPreset: PropTypes.string,
  modality: PropTypes.string,
  isHydratedForDerivedDisplaySet: PropTypes.bool,
  isTracked: PropTypes.bool,
  onClickUntrack: PropTypes.func,
  countIcon: PropTypes.string,
  thumbnailType: PropTypes.oneOf(['thumbnail', 'thumbnailTracked', 'thumbnailNoImage']),
  canReject: PropTypes.bool,
  onReject: PropTypes.func,
  ThumbnailMenuItems: PropTypes.func,
};

export { Thumbnail };
